import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import {Document as PDF, Page, pdfjs} from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import Iconify from '../../theme/iconify/iconify';

import Pagination from '../util/Pagination';
import Notification from '../util/Notification';
import ConfirmationModal from '../util/ConfirmationModal';
import {mislecAPI} from "../../util/axios";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function Overview({ document, updateUser }) {
  const [numPages, setNumPages] = React.useState();
  const [pageNumber, setPageNumber] = React.useState(1);
  const [loaded, setLoaded] = React.useState(false);
  const [removeDocumentModal, setRemoveDocumentModal] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState(null);
  const [severity, setSeverity] = React.useState(null);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const onDocumentLoadSuccess = ({ numPages }) => {
    for (let i = 1; i <= numPages; i++) {
      setPageNumber(i);
    }
    setPageNumber(1);
    setNumPages(numPages);
    setLoaded(true);
  }

  function changePage(offset) {
    setPageNumber(prevPageNumber => prevPageNumber + offset);
  }

  const removeDocument = () => {
    //  Remove document from the server
    mislecAPI.delete(`/api/document/${document._id}`)
      .then(res => {
        //  TODO
        //  Notify user of successful removal
        setRemoveDocumentModal(false);
        setMessage(t(res.data.detail));
        setSeverity('success');
        setOpen(true);
        setTimeout(() => {
          navigate('/documents');
        }, 2500);
      })
      .catch(error => {
        //  Notify user of error
        setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
        setSeverity('error');
        setOpen(true);

        //  Expired session
        if (error.response)
          if (error.response.data.detail === 'api.auth.verify.401')
            updateUser(null);
      });
  }

  const embed = () => {
    //  Embed document
    mislecAPI.post(`/api/document/${document._id}/embed`, {})
      .then(res => {
        setMessage(t(res.data.detail));
        setSeverity('success');
        setOpen(true);
      })
      .catch(error => {
        //  Notify user of error
        setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
        setSeverity('error');
        setOpen(true);

        //  Expired session
        if (error.response)
          if (error.response.data.detail === 'api.auth.verify.401')
            updateUser(null);
      });
  }

  return (
    <>
      <Grid container justifyContent='center' alignItems='center'>
        <Grid item xs={12} sx={{ position: 'relative' }}>
          <PDF
            file={`data:application/pdf;base64,${document.original}`}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            {loaded && (
              <Grid
                container
                justifyContent='flex-end'
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  mt: 2,
                  mr: 2,
                  pb: 2,
                  zIndex: 1,
                  width: 'fit-content',
                  opacity: {
                    xs: 1,
                    lg: 0.4
                  },
                  '&:hover': {
                    opacity: 1
                  }
                }}
              >
                <Paper elevation={2} sx={{ border: (theme) => `solid 1px ${theme.palette.divider}` }}>
                  <Tooltip title={t('document.tabs.overview.tooltips.remove')} arrow>
                    <IconButton
                      color='error'
                      onClick={() => setRemoveDocumentModal(true)}
                    >
                      <Iconify icon='eva:trash-outline' />
                    </IconButton>
                  </Tooltip>
                  <Box
                    component='span'
                    sx={{
                      display: 'inline',
                      alignSelf: 'center',
                      fontSize: '0.875rem',
                      borderRight: (theme) => `solid 2px ${theme.palette.divider}`,
                    }}
                  />
                  <Tooltip title={t('document.tabs.overview.tooltips.embed')} arrow>
                    <IconButton
                      color='inherit'
                      onClick={() => embed()}
                    >
                      <Iconify icon='icomoon-free:embed2' />
                    </IconButton>
                  </Tooltip>
                  <Box
                    component='span'
                    sx={{
                      display: 'inline',
                      alignSelf: 'center',
                      fontSize: '0.875rem',
                      borderRight: (theme) => `solid 2px ${theme.palette.divider}`,
                    }}
                  />
                  <a
                    id='mISLec_document_download'
                    href={`data:application/pdf;base64,${document.original}`}
                    target='_blank'
                    rel='noreferrer'
                    download={document.filename}
                  >
                    <Tooltip title={t('document.tabs.overview.tooltips.download')} arrow>
                      <IconButton
                        color='inherit'
                      >
                        <Iconify icon='material-symbols:download' />
                      </IconButton>
                    </Tooltip>
                  </a>
                </Paper>
              </Grid>
            )}
            {Array.from(
              new Array(numPages),
              (el, index) => (
                <Page
                  className={pageNumber === index + 1 ? 'react-pdf__active-page' : 'react-pdf__inactive-page'}
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  width={2000}
                />
              ))}
          </PDF>
          {loaded && (
            <Pagination page={pageNumber} total={numPages} changePage={changePage} />
          )}
          {!loaded && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '700px' }}>
              <CircularProgress />
            </Box>
          )}
        </Grid>
      </Grid>
      <ConfirmationModal
        type='remove'
        title={t('document.modals.removeDocument.title')}
        description={t('document.modals.removeDocument.description')}
        confirmationText={t('document.modals.removeDocument.confirmation')}
        confirm={removeDocument}
        open={removeDocumentModal}
        onClose={() => setRemoveDocumentModal(false)}
      />
      <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
    </>
  );
}

export default Overview;